/*
 * filter-bar.js — Horizontal filter bar enhancements (Secundo, Phase 2)
 *
 * Responsibilities (presentation only — does NOT touch FacetFiltersForm /
 * IntegerRange / PriceRange filtering logic):
 *   - Only one desktop filter popover (<details class="facet-pill">) open at a time.
 *   - Click-outside and Escape close the open popover.
 *   - On a filter change + Dawn's AJAX re-render: steppers/sliders (price,
 *     quantity, measurements) close on apply; multi-select list facets re-open
 *     so several options can be ticked in a row without flicker.
 *   - Sync the mobile "Vis N produkter" apply-button label with the live count.
 *
 * All hooks are additive; the markup contract in facets.liquid is untouched.
 */
(function () {
  const PILL_SELECTOR = 'details.facet-pill';

  /* ---- Single-open + click-outside + Esc for desktop pill popovers ---- */
  function closeAllPills(except) {
    document.querySelectorAll(PILL_SELECTOR + '[open]').forEach((details) => {
      if (details !== except) details.removeAttribute('open');
    });
  }

  function onToggle(event) {
    const details = event.target;
    if (!details.matches || !details.matches(PILL_SELECTOR)) return;
    if (details.open) closeAllPills(details);
  }

  function onDocumentClick(event) {
    const open = document.querySelector(PILL_SELECTOR + '[open]');
    if (!open) return;
    // Clicks inside any pill (summary or popover body) are fine.
    if (event.target.closest(PILL_SELECTOR)) return;
    closeAllPills(null);
  }

  function onKeydown(event) {
    if (event.key !== 'Escape' && event.key !== 'Esc') return;
    const open = document.querySelector(PILL_SELECTOR + '[open]');
    if (!open) return;
    closeAllPills(null);
    const summary = open.querySelector('summary');
    if (summary) summary.focus();
  }

  /*
   * Dawn's FacetFiltersForm.renderFilters() rewrites each .js-filter's innerHTML
   * on change, collapsing the <details> we interacted with. We want multi-select
   * LIST facets (brand, colour, condition…) to stay open so several options can
   * be ticked in a row, but steppers/sliders (price, quantity, measurements) to
   * close on apply. So on change we remember the open pill only if it's a list,
   * then re-open it after the AJAX swap. Steppers/sliders are simply left closed.
   */
  const STEPPER_SLIDER_SELECTOR = 'price-range, integer-range, .quantity-selector, .price-slider';
  let pendingOpenIndex = null;

  function rememberOpenPillOnChange(event) {
    pendingOpenIndex = null;
    const details = event.target.closest(PILL_SELECTOR);
    if (!details || !details.open) return;
    if (details.querySelector(STEPPER_SLIDER_SELECTOR)) return; // stepper/slider → let it close
    pendingOpenIndex = details.dataset.index || null;
  }

  function reopenListPill() {
    if (pendingOpenIndex == null) return;
    const sel = `${PILL_SELECTOR}[data-index="${CSS.escape(pendingOpenIndex)}"]`;
    const details = document.querySelector(sel);
    if (details && !details.open) details.setAttribute('open', '');
    pendingOpenIndex = null;
  }

  /* ---- Type-to-find search inside large facet dropdowns ---- */
  const ITEM_SELECTOR = '.facets__item, .mobile-facets__item';
  const LABEL_SELECTOR = '.facet-checkbox__label';

  // Resolve the scope that contains a search input's option list + show-more
  // button. Both desktop and mobile group wrappers carry `.js-filter`.
  function facetSearchScope(input) {
    return (
      input.closest('.js-filter') ||
      input.closest('.mobile-facets__submenu') ||
      input.parentElement
    );
  }

  function labelTextOf(item) {
    const label = item.querySelector(LABEL_SELECTOR);
    return (label ? label.textContent : item.textContent || '').toLowerCase();
  }

  function onFacetSearchInput(event) {
    const input = event.target;
    if (!input.matches || !input.matches('[data-facet-search]')) return;

    // Stop here: this is a client-side type-to-find box, not a real facet
    // field. Without this, the input event bubbles to Dawn's facet <form>,
    // whose debounced listener fires an AJAX facet fetch that re-renders the
    // list and wipes what you typed. Registered in capture phase (see init)
    // so this runs before the form's bubble-phase listener.
    event.stopPropagation();

    const scope = facetSearchScope(input);
    if (!scope) return;

    const query = input.value.toLowerCase().trim();
    const items = scope.querySelectorAll(ITEM_SELECTOR);
    const showMore = scope.querySelector('show-more-button');

    if (query === '') {
      // Restore the original collapsed state: clear our inline show/hide on
      // every row and re-hide the ones we revealed past the "show more"
      // threshold, then re-show the button.
      items.forEach((item) => {
        item.style.display = '';
        if (item.hasAttribute('data-facet-search-revealed')) {
          item.removeAttribute('data-facet-search-revealed');
          item.classList.add('hidden');
        }
      });
      if (showMore) showMore.style.display = '';
      return;
    }

    items.forEach((item) => {
      const matches = labelTextOf(item).indexOf(query) !== -1;
      if (matches) {
        // Matching rows are always visible while searching, even if they were
        // truncated by "show more" (Dawn's .hidden is display:none !important,
        // so inline style can't override it — drop the class and remember we
        // did, to restore it when the query clears).
        item.style.display = '';
        if (item.classList.contains('show-more-item') && item.classList.contains('hidden')) {
          item.classList.remove('hidden');
          item.setAttribute('data-facet-search-revealed', '');
        }
      } else {
        item.style.display = 'none';
      }
    });
    // The button is meaningless while filtering — every match is already shown.
    if (showMore) showMore.style.display = 'none';
  }

  /* ---- Mobile: keep the "Vis N produkter" apply label in sync ---- */
  function syncMobileApplyLabel() {
    const count = document.querySelector('.mobile-facets__count');
    const label = document.querySelector('.mobile-facets__apply-label');
    if (count && label) {
      const text = count.textContent.trim();
      // Only write when the value actually changed — writing unconditionally
      // re-triggers the MutationObserver below and freezes the page.
      if (text && label.textContent.trim() !== text) label.textContent = text;
    }
  }

  function init() {
    // toggle bubbles in modern browsers when capture is used.
    document.addEventListener('toggle', onToggle, true);
    document.addEventListener('click', onDocumentClick);
    document.addEventListener('keydown', onKeydown);
    document.addEventListener('change', rememberOpenPillOnChange, true);
    document.addEventListener('input', onFacetSearchInput, true);

    // After Dawn swaps facet markup on a filter change: pills come back collapsed,
    // so steppers/sliders are already closed; re-open the remembered list pill so
    // multi-select stays open. Also keep the mobile apply-label count in sync.
    const grid = document.getElementById('ProductGridContainer');
    if (grid && grid.parentNode) {
      const target = grid.parentNode;
      const observer = new MutationObserver(() => {
        // Disconnect while we mutate, or our own writes re-trigger us in a loop.
        observer.disconnect();
        closeAllPills(null);
        reopenListPill();
        syncMobileApplyLabel();
        observer.observe(target, { childList: true, subtree: true });
      });
      observer.observe(target, { childList: true, subtree: true });
    }

    syncMobileApplyLabel();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
